import { FormattedMessage, useIntl } from "react-intl";
import { type ReactNode, useState } from "react";
import { CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";

import { useForm } from "common/core/form";
import type { Payer } from "graphql_globals";
import Button from "common/core/button";
import { useMutation } from "util/graphql";
import { captureException } from "util/exception";
import TosV2 from "common/tos";

import AddCardMutation from "../graphql/add_card_to_organization_mutation.graphql";
import Styles from "./card_form.module.scss";
import { CardFailureModal } from "./card_failure_modal";
import { StripeCardElementsUIForReactHookFormV2, type StripeCardFormValues } from "./stripe_util";

const MESSAGES = {
  tosActionNew: {
    id: "d65fb63a-291e-4348-a73a-fb1f81489475",
    defaultMessage: "By changing your plan and clicking 'Submit Payment'",
  },
};

type CardFormProps = {
  cardDetails?: { name: string | null; last4: string | null } | null;
  organizationId: string;
  payer: Payer;
  onChangesSaved: () => void;
  submitButtonLabel?: ReactNode;
  showSubscriptionTos?: boolean;
  onClose?: () => void;
};

function useSubmitCard() {
  const addCardMutateFn = useMutation(AddCardMutation);
  const stripe = useStripe();
  const elements = useElements();

  return async (
    cardName: string,
    organizationId: string,
    payer: Payer,
    onChangesSaved: () => void,
  ) => {
    if (!elements || !stripe) {
      throw new Error(
        "verify component calling submitCardPayment is wrapped in a stripe <Element>",
      );
    }

    const cardElement = elements.getElement(CardNumberElement);

    try {
      const response = await stripe.createToken(cardElement!, { name: cardName });
      if (response.error) {
        throw new Error(response.error.message);
      }

      const token = response.token.id;

      await addCardMutateFn({
        variables: {
          input: {
            id: organizationId,
            cardToken: token,
            defaultPayer: payer,
          },
        },
      })
        .then(() => {
          onChangesSaved();
        })
        .catch((error) => {
          captureException(error);
          throw new Error("Error saving card details");
        });
    } catch (error) {
      captureException(error);
      throw new Error(`Error submitting card to Stripe`);
    }
  };
}

export function CardForm({
  organizationId,
  payer,
  onChangesSaved,
  submitButtonLabel,
  showSubscriptionTos = false,
  onClose,
}: CardFormProps) {
  const intl = useIntl();
  const [stripeError, setStripeError] = useState(false);
  const cardForm = useForm<StripeCardFormValues>();
  const submitCard = useSubmitCard();

  const handleCardFormSubmit = async (formValues: StripeCardFormValues) => {
    await submitCard(formValues.cardName, organizationId, payer, onChangesSaved).catch(() => {
      setStripeError(true);
    });
  };

  return (
    <form onSubmit={cardForm.handleSubmit(handleCardFormSubmit)}>
      <CardFailureModal stripeError={stripeError} setStripeError={setStripeError} />

      <StripeCardElementsUIForReactHookFormV2 form={cardForm} showLabels />

      {showSubscriptionTos && (
        <div data-automation-id="payment-subscription-tos" className={Styles.subscriptionTos}>
          <TosV2 actionText={intl.formatMessage(MESSAGES.tosActionNew)} />
        </div>
      )}

      <Button
        type="submit"
        buttonColor="action"
        variant="primary"
        data-automation-id="save-changes-button"
        isLoading={cardForm.formState.isSubmitting}
        className={Styles.button}
        disabled={!cardForm.formState.isValid || cardForm.formState.isSubmitting}
      >
        {submitButtonLabel ? (
          submitButtonLabel
        ) : (
          <FormattedMessage
            id="e7a89f11-9b88-4107-94b5-f42881f18dcb"
            defaultMessage="Save payment"
          />
        )}
      </Button>

      {onClose && (
        <Button
          buttonColor="dark"
          variant="tertiary"
          key="cancel"
          onClick={onClose}
          className={Styles.button}
        >
          <FormattedMessage id="5c4d3f96-c6c8-47cf-8ff6-7fa2fd99d69d" defaultMessage="Cancel" />
        </Button>
      )}
    </form>
  );
}
